/**
 * ShopCustomersView - Customer accounts view for Shop administration
 * Extracted from AdminShopTab.js in Phase 4D-4
 * 
 * Displays:
 * - Registered customer accounts list
 * - Customer name, email, order count, total spent, joined date
 * - Loading and empty states
 * 
 * Security: Read-only view, no customer modification capabilities
 */
import React from 'react';
import { Loader2, Layers } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';

/**
 * Format a date string for display (date only)
 * @param {string} dateStr - ISO date string
 * @returns {string} Formatted date (e.g., "08/12/2025")
 */
function formatJoinedDate(dateStr) {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB');
  } catch {
    return '-';
  }
}

/**
 * Format currency for customer total spent
 * Note: Using inline formatting to match original implementation exactly
 * @param {number} amount - Amount in GBP
 * @returns {string} Formatted amount (e.g., "£123.45")
 */
function formatSpent(amount) {
  return `£${(amount || 0).toFixed(2)}`;
}

/**
 * @param {Object} props
 * @param {Array} props.customers - Array of customer objects
 * @param {string} props.customers[].customer_id - Unique customer ID
 * @param {string} props.customers[].first_name - Customer first name
 * @param {string} props.customers[].last_name - Customer last name
 * @param {string} props.customers[].email - Customer email
 * @param {number} props.customers[].total_orders - Number of orders placed
 * @param {number} props.customers[].total_spent - Total amount spent (GBP)
 * @param {string} props.customers[].created_at - Account creation date (ISO string)
 * @param {boolean} props.loading - Whether customers are being fetched
 */
export function ShopCustomersView({ customers, loading }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Shop Customers</CardTitle>
        <CardDescription>View registered customer accounts and their order history</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Layers className="h-12 w-12 mx-auto mb-4 text-slate-300" />
            <p>No registered customers yet</p>
            <p className="text-sm">Customers can create accounts during checkout</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Orders</TableHead>
                <TableHead>Total Spent</TableHead>
                <TableHead>Joined</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((customer) => (
                <TableRow key={customer.customer_id}>
                  <TableCell className="font-medium">
                    {customer.first_name} {customer.last_name}
                  </TableCell>
                  <TableCell>{customer.email}</TableCell>
                  <TableCell>{customer.total_orders || 0}</TableCell>
                  <TableCell>{formatSpent(customer.total_spent)}</TableCell>
                  <TableCell>
                    {formatJoinedDate(customer.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default ShopCustomersView;
